import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import {useCallback, useEffect, useState} from "react";
import {deleteUser, getAllUsers, updateUser} from "../../api/user/userApiClient";
import SoftAlert from "../../components/Alert";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import UserFormDialog from "../../components/UserFormDialog";

import SoftInput from "../../components/SoftInput";
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {FormControl, IconButton, MenuItem, Select} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {Delete as DeleteIcon, Edit as EditIcon, LockReset as LockResetIcon} from "@mui/icons-material";
import {deleteAdmin, getAllAdmins, updateAdmin} from "../../api/admin/adminApiClient";
import {deleteBranchAdmin, getAllBranchAdmins, updateBranchAdmin} from "../../api/branchadmin/branchAdminApiClient";
import EnhancedTable from "../../components/EnhancedTable";
import DataTable, {renderBase64ImageCell, renderPill} from "../../components/DataTable";
import {getStatusColor, getStatusDisplayName, getUserRoleColor, getRoleDisplayName} from "../../utils/statusUtils";
import ResetConfirmDialog from "./components/ResetConfirmDialog";
import {requestPasswordReset} from "../../api/auth/authApiClient";
import {showSuccess, showError} from "../../utils/notificationUtils";
import {useRayzayUIController} from "../../context";

function Overview() {
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;
    const loggedInUserRole = loggedInUserDetail?.userRole;
    
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToEdit, setUserToEdit] = useState(null);
    const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedUserType, setSelectedUserType] = useState('ALL');
    const [isLoading, setIsLoading] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [userToReset, setUserToReset] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const isOnChildRoute = location.pathname.includes('/users/') && location.pathname !== '/users';

    // Filter user types based on logged-in user's role
    const getAllUserTypes = () => [
        {value: 'ALL', label: 'All Users'},
        {value: 'GUARDIAN', label: 'Guardians'},
        {value: 'STAFF', label: 'Staff'},
        {value: 'ADMIN', label: 'Admins'},
        {value: 'BRANCH_ADMIN', label: 'Branch Admins'},
        {value: 'SYSTEM_ADMIN', label: 'System Admins'}
    ];

    const userTypes = (() => {
        const allTypes = getAllUserTypes();
        
        // SYSTEM_ADMIN sees all user types
        if (loggedInUserRole === 'SYSTEM_ADMIN') {
            return allTypes;
        }
        
        // ADMIN sees all except SYSTEM_ADMIN and ADMIN
        if (loggedInUserRole === 'ADMIN') {
            return allTypes.filter(type => 
                type.value !== 'SYSTEM_ADMIN' && type.value !== 'ADMIN'
            );
        }
        
        // Default: return all types (for other roles)
        return allTypes;
    })();

    const loggedInUserId = loggedInUserDetail?.id;
    const columns = [
        {
            name: "first Name",
            value: "firstName",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "Last Name",
            value: "lastName",
            align: "left",
            width: "10",
            cell: ""
        },
        {
            name: "email",
            value: "email",
            align: "left",
            width: "20",
            cell: ""
        },
        {
            name: "phone No",
            value: "phoneNumber",
            align: "left",
            width: "10",
            cell: ""
        },
        {name: "first Name", value: "firstName", align: "left", width: "10"},
        {name: "Last Name", value: "lastName", align: "left", width: "10"},
        {name: "email", value: "email", align: "left", width: "20"},
        {name: "phone No", value: "phoneNumber", align: "left", width: "10"},
        {
            name: "position",
            value: "userRole",
            align: "center",
            width: "10",
        },
        {
            name: "status",
            value: "status",
            align: "center",
            width: "10",
        },
        {
            name: "Actions",
            value: "action",
            align: "center",
            width: "10",
            renderCell: (row) => {
                const isCurrentUser = loggedInUserId && row.id === loggedInUserId;
                const isSystemAdmin = row.userRole === "SYSTEM_ADMIN";
                return (
                    <>
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            disabled={isSystemAdmin || isCurrentUser}
                            title={isCurrentUser ? "Cannot delete your own account" : "Delete"}
                        >
                            <DeleteIcon/>
                        </IconButton>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditClick(row)}
                            disabled={isSystemAdmin || isCurrentUser}
                            title={isCurrentUser ? "Cannot edit your own account here" : "Edit"}
                        >
                            <EditIcon/>
                        </IconButton>
                    </>
                );
            },
        },
        {
            name: "Reset Password",
            value: "reset",
            align: "center",
            width: "10",
            renderCell: (row) => {
                const isAllowed = ["GUARDIAN", "STAFF", "BRANCH_ADMIN"].includes(row.userRole);
                const isCurrentUser = loggedInUserId && row.id === loggedInUserId;
                return (
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleResetClick(row)}
                        disabled={!isAllowed || isCurrentUser}
                        title={isCurrentUser ? "Cannot reset your own password here" : "Reset Password"}
                    >
                        <LockResetIcon/>
                    </IconButton>
                );
            },
        },
    ];

    const loadUsers = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            let response;
            if (selectedUserType === 'ALL') {
                response = await getAllUsers();
            } else if (selectedUserType === 'ADMIN' || selectedUserType === 'SYSTEM_ADMIN') {
                response = await getAllAdmins();
            } else if (selectedUserType === 'BRANCH_ADMIN') {
                response = await getAllBranchAdmins();
            } else {
                response = await getAllUsers();
                response.data = response.data.filter(user => user.userRole === selectedUserType);
            }
            setUsers(response.data);
            setFilteredUsers(response.data);
        } catch (error) {
            console.error('Error loading users:', error);
            setError('Failed to load users. Please try again.');
            setShowAlert(true);
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedUserType]);

    useEffect(() => {
        loadUsers().then();
    }, [loadUsers]);

    const handleUserTypeChange = (event) => {
        setSelectedUserType(event.target.value);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (userToDelete.userRole === 'BRANCH_ADMIN') {
                await deleteBranchAdmin(userToDelete.id);
            } else if (userToDelete.userRole === 'ADMIN' || userToDelete.userRole === 'SYSTEM_ADMIN') {
                await deleteAdmin(userToDelete.id);
            } else {
                await deleteUser(userToDelete.id);
            }
            
            await loadUsers();
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        } catch (err) {
            setError("Failed to delete user");
            setShowAlert(true);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleResetClick = (user) => {
        setUserToReset(user);
        setResetDialogOpen(true);
    };

    const handleResetConfirm = async () => {
        try {
            if (userToReset?.email) {
                const resp = await requestPasswordReset(userToReset.email);
                const msg = resp?.data?.message || "Password reset to default successful";
                showSuccess(msg);
            }
        } catch (err) {
            showError(err?.message || "Failed to request password reset");
            setShowAlert(true);
        } finally {
            setResetDialogOpen(false);
            setUserToReset(null);
        }
    };

    const handleResetCancel = () => {
        setResetDialogOpen(false);
        setUserToReset(null);
    };

    const handleEditClick = (user) => {
        setUserToEdit(user);
        setIsEdit(true);
        setEditAddDialogOpen(true);
    };

    const handleEditUser = async (formData) => {
        if (formData.userRole === 'BRANCH_ADMIN') {
            await updateBranchAdmin(userToEdit.id, formData);
        } else if (formData.userRole === 'ADMIN' || formData.userRole === 'SYSTEM_ADMIN') {
            await updateAdmin(userToEdit.id, formData);
        } else {
            await updateUser(userToEdit.id, formData);
        }
        
        await loadUsers();
        setEditAddDialogOpen(false);
        setUserToEdit(null);
    };

    const handleCloseDialog = () => {
        setDeleteDialogOpen(false)
        setEditAddDialogOpen(false);
        setUserToEdit(null);
        setUserToDelete(null);
    }

    const handleFilterUser = (value) => {
        let filtered = users.filter(user => {
            return user.firstName.toLowerCase().includes(value.toLowerCase()) ||
                user.lastName.toLowerCase().includes(value.toLowerCase()) ||
                user.email.toLowerCase().includes(value.toLowerCase()) ||
                user.phoneNumber.toLowerCase().includes(value.toLowerCase());
        });
        setFilteredUsers(filtered);
    }

    function handleOnSubmit(formData) {
        if (isEdit) {
            handleEditUser(formData).then();
            setEditAddDialogOpen(false);
        }
    }

    return (
        <DashboardLayout>
            <SoftBox pt={2} px={2} width="100%">
                <SoftBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
                    <SoftBox display="flex" alignItems="center" gap={2} flexShrink={0}>
                        {isOnChildRoute && (
                            <IconButton
                                onClick={() => navigate('/users')}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                            >
                                <ArrowBackIcon/>
                            </IconButton>
                        )}
                        <SoftTypography variant="h6" fontWeight="medium">
                            {isOnChildRoute ? 'User Details' : 'Users'}
                        </SoftTypography>
                    </SoftBox>
                    {!isOnChildRoute && (
                        <>
                            <SoftBox flexGrow={1} maxWidth={560}>
                                <SoftInput
                                    placeholder="Type here to filter by name, email or phone"
                                    icon={{component: "search", direction: "left"}}
                                    onChange={(event) => handleFilterUser(event.target.value)}
                                />
                            </SoftBox>
                            <FormControl size="small" sx={{minWidth: 180, flexShrink: 0}}>
                                <Select
                                    value={selectedUserType}
                                    onChange={handleUserTypeChange}
                                >
                                    {userTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </>
                    )}
                </SoftBox>
            </SoftBox>
            <SoftBox p={2} width="100%">
                {isOnChildRoute ? (
                    <Outlet/>
                ) : (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <EnhancedTable
                                TableComponent={DataTable}
                                isLoading={isLoading}
                                data={filteredUsers}
                                loadingMessage="Loading users..."
                                noDataMessage="No users found"
                                columns={columns}
                            />
                        </Grid>
                    </Grid>
                )}
            </SoftBox>
            {error && (
                <SoftAlert
                    color="error"
                    dismissible={false}
                    timeout={10}
                    mount={showAlert}
                    onClose={() => {
                        setError(null);
                        setShowAlert(false);
                    }}
                >
                    {error}
                </SoftAlert>
            )}
            <DeleteConfirmDialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onDelete={handleDeleteConfirm}
                warningMessage={`Are you sure you want to delete ${userToDelete?.firstName || ""} ${userToDelete?.lastName || ""}? This action cannot be undone.`}
            />
            <UserFormDialog
                open={editAddDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleOnSubmit}
                user={userToEdit}
                isEdit={isEdit}
                entityLabel="User"
                defaultUserRole={userToEdit?.userRole || "ADMIN"}
                minAge={18}
                requireProfilePicture={false}
            />
            <ResetConfirmDialog
                open={resetDialogOpen}
                onClose={handleResetCancel}
                onConfirm={handleResetConfirm}
                message={`Are you sure you want to reset password for ${userToReset?.firstName || ""} ${userToReset?.lastName || ""}?`}
            />
        </DashboardLayout>
    );
}

export default Overview;
